import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// ─── Types ───────────────────────────────────────────────────────────────────

export type RiskLevel = 'high' | 'medium' | 'low';
export type AlertType = 'high_rejection' | 'no_recent_orders' | 'pending_overdue' | 'high_damage';
export type AlertSeverity = 'critical' | 'warning';

export interface SupplierGovernanceMetrics {
    supplier_id: string;
    supplier_name: string;
    status: string;
    categories: string[];
    region: string | null;
    payment_term_days: number | null;
    supports_damage_return: boolean | null;
    // Procurement aggregates (within date range, approved statuses only)
    total_procurements: number;         // all procurements in range
    approved_procurements: number;      // governance-approved only
    completed_procurements: number;
    rejected_procurements: number;
    pending_procurements: number;
    in_progress_procurements: number;
    // Rates (based on total in range)
    completion_rate: number;
    rejection_rate: number;
    // Financial & volume (approved only)
    total_value: number;
    approved_quantity_kg: number;       // approved KG for contribution calc
    total_quantity: number;             // all KG in range
    // KPI
    kg_contribution_pct: number;        // supplier approved KG / total all-supplier approved KG × 100
    // Damage metrics (from procurement_quality, approved procurements only)
    total_gross_damage_kg: number;      // sum of damage_kg
    total_compensation_kg: number;      // sum of compensation_kg
    total_net_damage_kg: number;        // gross - compensation
    gross_damage_pct: number;           // (gross / approved_quantity_kg) * 100
    compensation_pct: number;           // (compensation / approved_quantity_kg) * 100
    net_damage_pct: number;             // (net / approved_quantity_kg) * 100
    // Temporal
    latest_order_date: string | null;
    // Governance
    risk_level: RiskLevel;
}

export interface GovernanceSummary {
    total_active_suppliers: number;
    total_inactive_suppliers: number;
    high_risk_count: number;
    medium_risk_count: number;
    low_risk_count: number;
    total_sourcing_value: number;
    total_approved_kg: number;
    avg_rejection_rate: number;
    avg_completion_rate: number;
    total_supplier_procurements: number;
    avg_gross_damage_pct: number;
    avg_compensation_pct: number;
    avg_net_damage_pct: number;
}

export interface RiskAlert {
    supplier_id: string;
    supplier_name: string;
    alert_type: AlertType;
    description: string;
    severity: AlertSeverity;
}

export interface GovernanceData {
    summary: GovernanceSummary;
    supplierMetrics: SupplierGovernanceMetrics[];
    riskAlerts: RiskAlert[];
    dateRange: { startDate: Date; endDate: Date };
}

export interface GovernanceParams {
    startDate: Date;
    endDate: Date;
}

// ─── Approved statuses ────────────────────────────────────────────────────────
// Statuses that count as "governance approved" for KG contribution & value sums

const APPROVED_STATUSES = new Set([
    'completed',
    'delivered',
    // Finance approval variants
    'approved_finance',
    'finance_approved',
    'approved',
    // Warehouse approval variants
    'approved_warehouse',
    'warehouse_approved',
    // Quality approval variants
    'approved_quality',
    'quality_approved',
    'quality_graded',
]);

const REJECTED_STATUSES = new Set(['rejected', 'cancelled']);

const PENDING_STATUSES = new Set([
    'initiated',
    'pending_finance',
    'pending_warehouse',
    'pending_quality',
]);

// ─── Risk helpers ─────────────────────────────────────────────────────────────

const computeRiskLevel = (
    total: number,
    completion_rate: number,
    rejection_rate: number,
    net_damage_pct: number,
    status: string,
    latest_order_date: string | null,
): RiskLevel => {
    if (status !== 'active') return 'low';
    
    // HIGH RISK conditions
    if (rejection_rate > 20) return 'high';
    if (net_damage_pct > 15) return 'high';  // Quality risk: high damage
    if (total > 0 && completion_rate < 50) return 'high';
    
    // MEDIUM RISK conditions
    if (rejection_rate > 10) return 'medium';
    if (net_damage_pct > 8) return 'medium';  // Quality risk: elevated damage
    if (total > 0 && completion_rate < 75) return 'medium';
    if (total === 0) return 'medium';
    if (latest_order_date) {
        const daysSince =
            (Date.now() - new Date(latest_order_date).getTime()) / (1000 * 60 * 60 * 24);
        if (daysSince > 60) return 'medium';
    }
    return 'low';
};

// ─── Core fetch ───────────────────────────────────────────────────────────────

const fetchGovernanceData = async (params: GovernanceParams): Promise<GovernanceData> => {
    const { startDate, endDate } = params;

    // Extend endDate to end of day for inclusive range
    const endOfDay = new Date(endDate);
    endOfDay.setHours(23, 59, 59, 999);

    const SIXTY_DAYS_AGO = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000);

    const [suppliersRes, procurementsRes, qualityRes] = await Promise.all([
        supabase
            .from('suppliers')
            .select(
                'id, name, status, region, payment_term_days, supports_damage_return, supplier_categories(category_name)',
            )
            .order('name'),
        supabase
            .from('procurements')
            .select(
                'id, source_id, source_type, status, total_amount, total_quantity, created_at, expected_delivery_date, delivery_date, rejection_source',
            )
            .eq('source_type', 'supplier')
            .not('source_id', 'is', null)
            .gte('created_at', startDate.toISOString())
            .lte('created_at', endOfDay.toISOString()),
        supabase
            .from('procurement_quality')
            .select('procurement_id, damage_kg, compensation_kg')
            .gte('created_at', startDate.toISOString())
            .lte('created_at', endOfDay.toISOString()),
    ]);

    if (suppliersRes.error) throw suppliersRes.error;
    if (procurementsRes.error) throw procurementsRes.error;
    if (qualityRes.error) throw qualityRes.error;

    const suppliers = suppliersRes.data ?? [];
    const procurements = procurementsRes.data ?? [];
    const qualityData = qualityRes.data ?? [];

    // Create quality lookup map
    const qualityMap = new Map<string, { damage_kg: number; compensation_kg: number }>();
    qualityData.forEach((q) => {
        qualityMap.set(q.procurement_id, {
            damage_kg: q.damage_kg ?? 0,
            compensation_kg: q.compensation_kg ?? 0,
        });
    });

    // ── Global totals for KG contribution denominator ──
    const globalApprovedKg = procurements
        .filter((p) => APPROVED_STATUSES.has(p.status))
        .reduce((sum, p) => sum + (p.total_quantity ?? 0), 0);

    // ── Per-supplier metrics ──
    const supplierMetrics: SupplierGovernanceMetrics[] = suppliers.map((supplier) => {
        const sp = procurements.filter((p) => p.source_id === supplier.id);
        const total = sp.length;

        const approved = sp.filter((p) => APPROVED_STATUSES.has(p.status));
        const rejected_count = sp.filter((p) => REJECTED_STATUSES.has(p.status)).length;
        const pending_count = sp.filter((p) => PENDING_STATUSES.has(p.status)).length;
        const in_progress_count = sp.filter((p) => p.status === 'in_progress').length;
        const completed_count = sp.filter((p) =>
            ['completed', 'delivered'].includes(p.status),
        ).length;

        const completion_rate = total > 0 ? (completed_count / total) * 100 : 0;
        const rejection_rate = total > 0 ? (rejected_count / total) * 100 : 0;

        // Value = sum of approved procurements only
        const total_value = approved.reduce((sum, p) => sum + (p.total_amount ?? 0), 0);
        // Quantity totals
        const approved_quantity_kg = approved.reduce((sum, p) => sum + (p.total_quantity ?? 0), 0);
        const total_quantity = sp.reduce((sum, p) => sum + (p.total_quantity ?? 0), 0);

        // Damage metrics from quality data (approved procurements only)
        const total_gross_damage_kg = approved.reduce((sum, p) => {
            const quality = qualityMap.get(p.id);
            return sum + (quality?.damage_kg ?? 0);
        }, 0);
        const total_compensation_kg = approved.reduce((sum, p) => {
            const quality = qualityMap.get(p.id);
            return sum + (quality?.compensation_kg ?? 0);
        }, 0);
        const total_net_damage_kg = total_gross_damage_kg - total_compensation_kg;

        // Calculate damage percentages (use approved_quantity_kg as denominator)
        const gross_damage_pct = approved_quantity_kg > 0 
            ? (total_gross_damage_kg / approved_quantity_kg) * 100 
            : 0;
        const compensation_pct = approved_quantity_kg > 0 
            ? (total_compensation_kg / approved_quantity_kg) * 100 
            : 0;
        const net_damage_pct = approved_quantity_kg > 0 
            ? (total_net_damage_kg / approved_quantity_kg) * 100 
            : 0;

        // KG contribution %
        const kg_contribution_pct =
            globalApprovedKg > 0 ? (approved_quantity_kg / globalApprovedKg) * 100 : 0;

        const sortedDates = sp
            .map((p) => p.created_at)
            .filter(Boolean)
            .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
        const latest_order_date = sortedDates[0] ?? null;

        const risk_level = computeRiskLevel(
            total,
            completion_rate,
            rejection_rate,
            net_damage_pct,
            supplier.status,
            latest_order_date,
        );

        const categories = (
            (supplier.supplier_categories as { category_name: string }[]) ?? []
        )
            .map((c) => c.category_name)
            .filter(Boolean);

        return {
            supplier_id: supplier.id,
            supplier_name: supplier.name,
            status: supplier.status,
            categories,
            region: supplier.region ?? null,
            payment_term_days: supplier.payment_term_days ?? null,
            supports_damage_return: supplier.supports_damage_return ?? null,
            total_procurements: total,
            approved_procurements: approved.length,
            completed_procurements: completed_count,
            rejected_procurements: rejected_count,
            pending_procurements: pending_count,
            in_progress_procurements: in_progress_count,
            completion_rate,
            rejection_rate,
            total_value,
            approved_quantity_kg,
            total_quantity,
            kg_contribution_pct,
            total_gross_damage_kg,
            total_compensation_kg,
            total_net_damage_kg,
            gross_damage_pct,
            compensation_pct,
            net_damage_pct,
            latest_order_date,
            risk_level,
        };
    });

    // ── Summary ──
    const active = supplierMetrics.filter((s) => s.status === 'active');
    const inactive = supplierMetrics.filter((s) => s.status !== 'active');

    const summary: GovernanceSummary = {
        total_active_suppliers: active.length,
        total_inactive_suppliers: inactive.length,
        high_risk_count: active.filter((s) => s.risk_level === 'high').length,
        medium_risk_count: active.filter((s) => s.risk_level === 'medium').length,
        low_risk_count: active.filter((s) => s.risk_level === 'low').length,
        total_sourcing_value: supplierMetrics.reduce((sum, s) => sum + s.total_value, 0),
        total_approved_kg: globalApprovedKg,
        avg_rejection_rate:
            active.length > 0
                ? active.reduce((sum, s) => sum + s.rejection_rate, 0) / active.length
                : 0,
        avg_completion_rate:
            active.length > 0
                ? active.reduce((sum, s) => sum + s.completion_rate, 0) / active.length
                : 0,
        total_supplier_procurements: procurements.length,
        avg_gross_damage_pct:
            active.length > 0
                ? active.reduce((sum, s) => sum + s.gross_damage_pct, 0) / active.length
                : 0,
        avg_compensation_pct:
            active.length > 0
                ? active.reduce((sum, s) => sum + s.compensation_pct, 0) / active.length
                : 0,
        avg_net_damage_pct:
            active.length > 0
                ? active.reduce((sum, s) => sum + s.net_damage_pct, 0) / active.length
                : 0,
    };

    // ── Risk alerts ──
    const riskAlerts: RiskAlert[] = [];

    for (const s of active) {
        if (s.rejection_rate > 25) {
            riskAlerts.push({
                supplier_id: s.supplier_id,
                supplier_name: s.supplier_name,
                alert_type: 'high_rejection',
                description: `Critical rejection rate of ${s.rejection_rate.toFixed(1)}% — ${s.rejected_procurements} of ${s.total_procurements} orders rejected.`,
                severity: 'critical',
            });
        } else if (s.rejection_rate > 10) {
            riskAlerts.push({
                supplier_id: s.supplier_id,
                supplier_name: s.supplier_name,
                alert_type: 'high_rejection',
                description: `Elevated rejection rate of ${s.rejection_rate.toFixed(1)}%.`,
                severity: 'warning',
            });
        }

        if (s.total_procurements === 0) {
            riskAlerts.push({
                supplier_id: s.supplier_id,
                supplier_name: s.supplier_name,
                alert_type: 'no_recent_orders',
                description: 'No procurement orders recorded in the selected date range.',
                severity: 'warning',
            });
        } else if (
            s.latest_order_date &&
            new Date(s.latest_order_date) < SIXTY_DAYS_AGO
        ) {
            riskAlerts.push({
                supplier_id: s.supplier_id,
                supplier_name: s.supplier_name,
                alert_type: 'no_recent_orders',
                description: `No new orders in the past 60 days. Last order: ${new Date(
                    s.latest_order_date,
                ).toLocaleDateString()}.`,
                severity: 'warning',
            });
        }

        if (s.pending_procurements > 5) {
            riskAlerts.push({
                supplier_id: s.supplier_id,
                supplier_name: s.supplier_name,
                alert_type: 'pending_overdue',
                description: `${s.pending_procurements} orders currently stuck in pending state.`,
                severity: 'warning',
            });
        }

        // Damage-based alerts
        if (s.net_damage_pct > 15) {
            riskAlerts.push({
                supplier_id: s.supplier_id,
                supplier_name: s.supplier_name,
                alert_type: 'high_damage',
                description: `Critical net damage rate of ${s.net_damage_pct.toFixed(1)}% — ${formatKg(s.total_net_damage_kg)} net damage from ${formatKg(s.approved_quantity_kg)} approved.`,
                severity: 'critical',
            });
        } else if (s.net_damage_pct > 8) {
            riskAlerts.push({
                supplier_id: s.supplier_id,
                supplier_name: s.supplier_name,
                alert_type: 'high_damage',
                description: `Elevated net damage rate of ${s.net_damage_pct.toFixed(1)}%.`,
                severity: 'warning',
            });
        }
    }

    riskAlerts.sort((a, b) =>
        a.severity === 'critical' && b.severity !== 'critical' ? -1 : 1,
    );

    return { summary, supplierMetrics, riskAlerts, dateRange: params };
};

// ─── Helper for formatting KG in alerts ──────────────────────────────────────

const formatKg = (v: number): string => {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M kg`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K kg`;
    return `${v.toFixed(0)} kg`;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useSupplierGovernance = (params: GovernanceParams) =>
    useQuery({
        queryKey: [
            'supplier-governance',
            params.startDate.toISOString().slice(0, 10),
            params.endDate.toISOString().slice(0, 10),
        ],
        queryFn: () => fetchGovernanceData(params),
        staleTime: 5 * 60 * 1000,
    });
